import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, Plus, Search, Users, X } from 'lucide-react';
import { useProjects, useAvailableProjects, useDistrictProjects } from '@/hooks/use-projects';
import { ProjectCard } from '@/components/projects/ProjectCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  SegmentedTabs,
  SegmentedTabsContent,
  SegmentedTabsList,
  SegmentedTabsTrigger,
} from '@/components/ui/segmented-tabs';
import { stripHtmlToPlainText } from '@/lib/strip-html';
import { getProjectAvenueName } from '@/lib/project-avenue';
import { useCommunity } from '@/hooks/use-community';
import { communityHasAvenues } from '@/lib/community-platform-tags';
import type { Project } from '@/types';

type BrowseMode = 'community' | 'district';

const ALL_AVENUES = 'all';

function matchesDistrictSearch(project: Project, query: string) {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  const searchable = [
    project.name,
    project.location,
    project.happeningFor,
    project.hostClub,
    getProjectAvenueName(project),
    project.creator?.firstName,
    project.creator?.lastName,
    stripHtmlToPlainText(project.description),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return searchable.includes(normalizedQuery);
}

export default function ProjectsPage() {
  const { communityId } = useParams<{ communityId: string }>();
  const { community: communityPayload } = useCommunity(communityId);
  const isDistrictProjectsEnabled = communityHasAvenues(communityPayload?.community?.platformTags);
  const [browseMode, setBrowseMode] = useState<BrowseMode>('community');
  const [districtSearch, setDistrictSearch] = useState('');
  const [selectedAvenue, setSelectedAvenue] = useState(ALL_AVENUES);
  const { projects, isLoading: isLoadingOwned } = useProjects(communityId || '');
  const { projects: sharedProjects, isLoading: isLoadingShared } = useAvailableProjects(
    communityId || ''
  );
  const { projects: districtProjectsFromApi, isLoading: isLoadingDistrict } = useDistrictProjects(
    isDistrictProjectsEnabled ? communityId || '' : ''
  );

  const isLoadingCommunity = isLoadingOwned || isLoadingShared;

  const ownProjects = projects.filter((p) => p.communityId === communityId);
  const sharedFromOthers = sharedProjects.filter((p) => p.communityId !== communityId);

  const districtProjects = useMemo(() => {
    const byId = new Map<string, Project>();

    for (const project of districtProjectsFromApi) {
      byId.set(project.id, project);
    }

    for (const project of ownProjects) {
      if (project.isDistrictProject) {
        byId.set(project.id, project);
      }
    }

    return Array.from(byId.values()).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [districtProjectsFromApi, ownProjects]);

  const avenueOptions = useMemo(() => {
    const names = districtProjects
      .map((project) => getProjectAvenueName(project))
      .filter((name): name is string => Boolean(name));
    return [...new Set(names)].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [districtProjects]);

  const filteredDistrictProjects = useMemo(() => {
    return districtProjects.filter((project) => {
      const matchesAvenue =
        selectedAvenue === ALL_AVENUES || getProjectAvenueName(project) === selectedAvenue;
      return matchesAvenue && matchesDistrictSearch(project, districtSearch);
    });
  }, [districtProjects, districtSearch, selectedAvenue]);

  const hasDistrictFilters = districtSearch.trim().length > 0 || selectedAvenue !== ALL_AVENUES;

  const clearDistrictFilters = () => {
    setDistrictSearch('');
    setSelectedAvenue(ALL_AVENUES);
  };

  if (browseMode === 'community' && isLoadingCommunity) {
    return (
      <div className="flex h-[50vh] w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground mt-2">
            {browseMode === 'community'
              ? 'Community projects and initiatives.'
              : 'District director projects, including your unpublished drafts.'}
          </p>
        </div>
        {communityId && browseMode === 'community' && (
          <Button asChild>
            <Link to={`/${communityId}/projects/new`}>
              <Plus className="mr-2 h-4 w-4" />
              New Project
            </Link>
          </Button>
        )}
      </div>

      <SegmentedTabs
        value={browseMode}
        onValueChange={(value) => setBrowseMode(value as BrowseMode)}
        className="space-y-6"
      >
        <SegmentedTabsList className="w-full sm:w-fit">
          <SegmentedTabsTrigger value="community">Community Projects</SegmentedTabsTrigger>
          {isDistrictProjectsEnabled && (
            <SegmentedTabsTrigger value="district">District Projects</SegmentedTabsTrigger>
          )}
        </SegmentedTabsList>

        <SegmentedTabsContent value="community" className="space-y-8 mt-0">
          {isLoadingCommunity ? (
            <div className="flex h-[50vh] w-full items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : ownProjects.length === 0 && sharedFromOthers.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground mb-4">
                  No projects yet. Create your first project to get started.
                </p>
                {communityId && (
                  <Button asChild variant="outline">
                    <Link to={`/${communityId}/projects/new`}>
                      <Plus className="mr-2 h-4 w-4" />
                      Create Project
                    </Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
              {ownProjects.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold mb-4">Community Projects</h2>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {ownProjects.map((project) => (
                      <ProjectCard
                        key={project.id}
                        project={project}
                        communityId={communityId || ''}
                      />
                    ))}
                  </div>
                </div>
              )}

              {sharedFromOthers.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="h-5 w-5 text-muted-foreground" />
                    <h2 className="text-xl font-semibold">Shared With You</h2>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {sharedFromOthers.map((project) => (
                      <ProjectCard
                        key={project.id}
                        project={project}
                        communityId={communityId || ''}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </SegmentedTabsContent>

        <SegmentedTabsContent value="district" className="mt-0 space-y-6">
          {isLoadingDistrict && districtProjects.length === 0 ? (
            <div className="flex h-[50vh] w-full items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : districtProjects.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground">No district projects yet.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={districtSearch}
                    onChange={(event) => setDistrictSearch(event.target.value)}
                    placeholder="Search district projects..."
                    className="pl-9"
                  />
                </div>
                <Select value={selectedAvenue} onValueChange={setSelectedAvenue}>
                  <SelectTrigger className="w-full sm:w-[220px]">
                    <SelectValue placeholder="Filter by avenue" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_AVENUES}>All avenues</SelectItem>
                    {avenueOptions.map((avenueName) => (
                      <SelectItem key={avenueName} value={avenueName}>
                        {avenueName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {hasDistrictFilters && (
                  <Button type="button" variant="outline" onClick={clearDistrictFilters}>
                    <X className="mr-2 h-4 w-4" />
                    Clear
                  </Button>
                )}
              </div>

              {filteredDistrictProjects.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <p className="text-muted-foreground">
                      No district projects match your search or filters.
                    </p>
                    {hasDistrictFilters && (
                      <Button
                        type="button"
                        variant="outline"
                        className="mt-4"
                        onClick={clearDistrictFilters}
                      >
                        Clear filters
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredDistrictProjects.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      communityId={communityId || ''}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </SegmentedTabsContent>
      </SegmentedTabs>
    </div>
  );
}
